import {Engine} from "platform/engine/Engine";
import {XhrUtil} from "core/util/XhrUtil";
import {SetAuthStatusPayload} from "core/redux/auth/AuthReduxActions";
import Platform from "platform/Platform";
import {StoreState} from "core/redux/StoreState";
import Utils from "platform/util/Utils";
import {HttpReject} from "platform/network/http/Http";
import {BadRequestResponse} from "protocol/BadRequestResponse";
import {TradeSymbol} from "platform/protocol/trading/symbol/TradeSymbol";
import {
    AddSubscribedSymbol,
    FailedFetchSymbols,
    RemoveSubscribedSymbol,
    SetSortedSymbols,
    SetSubscribedSymbols,
    SetSymbols,
    SetSymbolTablesLS,
    SetSymbolTradeColumnPayload, SetTopSymbols,
    SymbolPayload,
    ToggleSymbolColumnSortPayload
} from "core/redux/symbols/SymbolsReduxActions";
import {TSMap} from "typescript-map";
import {SymbolSubscriptionInfo} from "protocol/symbol/SymbolSubscriptionInfo";
import {TradeColumn} from "enum/TradeColumn";
import {SortDirection} from "platform/enum/SortDirection";
import {Quote} from "platform/protocol/trading/Quote";
import {AddInboxMessage} from "core/redux/inbox/InboxReduxActions";
import InboxMessageType from "enum/InboxMessageType";
import InboxParameter from "enum/InboxParameter";
import Parameter from "platform/util/Parameter";
import {HideLoader, SetLoader} from "platform/redux/core/CoreActions";
import {LoaderType} from "platform/enum/LoaderType";
import {SymbolCategory} from "enum/SymbolCategory";
import {StorageKey} from "enum/StorageKey";
import {
    CategoryCounterState,
    SymbolReduxState,
    SymbolSelection,
    SymbolTableState
} from "core/redux/symbols/SymbolReduxState";
import {GetUserSubscribedSymbolsResponse} from "protocol/symbol/GetUserSubscribedSymbolsResponse";
import {QuotesReduxState} from "core/redux/quotes/QuotesReduxState";
import {SymbolTrend} from "platform/protocol/trading/SymbolTrend";
import {GetAllSymbolsResponse} from "protocol/symbol/GetAllSymbolsResponse";
import AppEngine from "core/engine/AppEngine";
import {ServiceType} from "enum/ServiceType";

export const SortSymbols = (column: TradeColumn, direction: SortDirection, quotes: TSMap<number, Quote>) => {
    return (s1: TradeSymbol, s2: TradeSymbol): number => {
        const q1: Quote = quotes.get(s1.SymbolId);
        const q2: Quote = quotes.get(s2.SymbolId);
        let result: number = 0;
        switch (column) {
            case TradeColumn.Symbol:
                result = Utils.compareString(s1.text, s2.text);
                break;
            case TradeColumn.Price:
                result = q1 && q2 && Utils.compareNumber(q1.ChangeInPercentage, q2.ChangeInPercentage);
                break;
            case TradeColumn.Chart1d:
                result = q1 && q2 && Utils.compareNumber(q1.ChangeInPercentage, q2.ChangeInPercentage);
                break;
            case TradeColumn.Buy:
                result = q1 && q2 && Utils.compareNumber(q1.Ask, q2.Ask);
                break;
            case TradeColumn.Sell:
                result = q1 && q2 && Utils.compareNumber(q1.Bid, q2.Bid);
                break;
            case TradeColumn.Trend:
                const st1: SymbolTrend = q1?.SymbolTrend || {};
                const st2: SymbolTrend = q2?.SymbolTrend || {};
                result = Utils.compareNumber(st1.BuyPositionsPercentage, st2.BuyPositionsPercentage);
                if (result === 0) {
                    result = Utils.compareNumber(st1.SellPositionsPercentage, st2.SellPositionsPercentage);
                }
                break;
        }
        return direction === SortDirection.Ask ? result : -result;
    };
};

type LSData = {

    tables: TSMap<string, SymbolTableState>;
};

export default class SymbolsEngine extends Engine {

    private static _instance: SymbolsEngine;

    public static instance(): SymbolsEngine {
        return this._instance || (this._instance = new this());
    }

    public async setup(): Promise<void> {
        await super.setup();
        const data: LSData = await this.deserializeLS();
        Platform.dispatch(SetSymbolTablesLS(data));
    }

    private deserializeLS = async (): Promise<LSData> => {
        const tables: TSMap<string, SymbolTableState> = await SymbolCategory.deserialize(StorageKey.SymbolsSort);
        return Promise.resolve({tables});
    }

    public doSetAuthStatus = async (payload: SetAuthStatusPayload): Promise<void> => {
        const {connected, accountType} = Platform.reduxState<StoreState>().auth;
        if (connected) {
            if (Utils.isNotNull(payload.authStatus.accountType) && accountType !== payload.authStatus.accountType) {
                this._logger.debug("Fetching symbols");
                let subscribedSymbolIds: number[] = [];
                const answer: [HttpReject, GetUserSubscribedSymbolsResponse] = await Utils.to(this.sendToAccount({}, "GetUserSubscribedSymbols"));
                if (answer[1]) {
                    subscribedSymbolIds = answer[1].SymbolIds || [];
                }
                this.sendToAccount(null, "GetAllSymbols").then((response: GetAllSymbolsResponse) => {
                    this._logger.debug("Symbols fetched");
                    const tradeSymbols: TradeSymbol[] = response.Symbols;
                    const quotesReduxState: QuotesReduxState = Platform.reduxState<StoreState>().quotes;
                    const symbolReduxState: SymbolReduxState = Platform.reduxState<StoreState>().symbols;
                    const symbols: TSMap<number, TradeSymbol> = new TSMap();
                    const categories: TSMap<string, TradeSymbol[]> = symbolReduxState.categories.clone();
                    const counters: TSMap<string, CategoryCounterState> = symbolReduxState.counters.clone();
                    const selectedSymbols: TSMap<string, SymbolSelection> = symbolReduxState.selectedSymbols.clone();
                    const tables: TSMap<string, SymbolTableState> = symbolReduxState.tables.clone();
                    const subscribedSymbols: TSMap<number, SymbolSubscriptionInfo> = new TSMap();
                    const myWatchSymbols: TradeSymbol[] = [];
                    const overview: TradeSymbol[] = [];
                    if (Utils.isArrayNotEmpty(tradeSymbols)) {
                        tradeSymbols.forEach((symbol: TradeSymbol) => {
                            let category: TradeSymbol[] = categories.get(symbol.category);
                            if (Utils.isNull(category)) {
                                categories.set(symbol.category, category = []);
                                if (!tables.has(symbol.category)) {
                                    tables.set(symbol.category, SymbolCategory.defaultTableState());
                                }
                            }
                            const SymbolId: number = parseInt(symbol.symbolId, 10);
                            const Symbol: TradeSymbol = {
                                ...symbol,
                                SymbolId
                            };
                            symbols.set(SymbolId, Symbol);
                            category.push(Symbol);
                            if (subscribedSymbolIds.indexOf(SymbolId) > -1) {
                                subscribedSymbols.set(SymbolId, {
                                    SymbolDisplayName: Symbol.symbol,
                                    SymbolId,
                                    ViewInBox: true,
                                    ViewInGrid: true,
                                });
                                myWatchSymbols.push(Symbol);
                            }
                        });
                        categories.forEach((Symbols: TradeSymbol[], categoryName: string) => {
                            counters.set(categoryName, {categoryName, count: Symbols.length});
                            const tableState: SymbolTableState = tables.get(categoryName);
                            Symbols.sort(SortSymbols(tableState.activeColumn, tableState.sort.get(tableState.activeColumn), quotesReduxState.quotes));
                            selectedSymbols.set(categoryName, {
                                symbol: Symbols[0],
                                index: 0
                            });
                        });
                        for (let i = subscribedSymbolIds.length - 1; i >= 0; i--) {
                            const symbol: TradeSymbol = symbols.get(subscribedSymbolIds[i]);
                            if (symbol) {
                                overview.push(symbol);
                            }
                        }
                    }
                    const myWatchTableState: SymbolTableState = tables.get(SymbolCategory.MyWatchlist);
                    myWatchSymbols.sort(SortSymbols(myWatchTableState.activeColumn, myWatchTableState.sort.get(myWatchTableState.activeColumn), quotesReduxState.quotes));
                    categories.set(SymbolCategory.MyWatchlist, myWatchSymbols);
                    selectedSymbols.set(SymbolCategory.MyWatchlist, {
                        symbol: myWatchSymbols[0],
                        index: 0
                    });
                    counters.set(SymbolCategory.MyWatchlist, {
                        categoryName: SymbolCategory.MyWatchlist,
                        count: myWatchSymbols.length
                    });
                    const countersSorted: TSMap<string, CategoryCounterState> = new TSMap<string, CategoryCounterState>();
                    [...counters.keys()].sort((sc1: SymbolCategory, sc2: SymbolCategory) => Utils.compareNumber(SymbolCategory.order(sc1), SymbolCategory.order(sc2)))
                        .forEach((sc: SymbolCategory) => {
                            countersSorted.set(sc, counters.get(sc));
                        });
                    Platform.dispatch(SetSymbols({symbols, overview, categories, counters: countersSorted, selectedSymbols, tables}));
                    Platform.dispatch(SetSubscribedSymbols({
                        symbols: subscribedSymbols
                    }));
                    // TODO Get top movers from server once it's ready
                    Platform.dispatch(SetTopSymbols({symbols: []}));
                    const appEngine: AppEngine = Platform.engine(ServiceType.App);
                    appEngine.tryExecuteDpkUrl();
                }).catch((reject: HttpReject) => {
                    this._logger.debug("Failed fetch symbols");
                    Platform.dispatch(FailedFetchSymbols({}));
                    if (reject && Utils.isNotNull(reject.response)) {
                        let response: BadRequestResponse;
                        try {
                            response = JSON.parse(reject.response);
                        } catch (e) {
                        }
                        if (Utils.isNull(response) || response.IsSecurityException) {
                            this._logger.warn("Failed fetch all symbols");
                        }
                    }
                });
            }
        }
    }

    public doSetSymbolTradeColumn = (payload: SetSymbolTradeColumnPayload) => {
        const {symbols, quotes} = Platform.reduxState<StoreState>();
        const {category, tradeColumn} = payload;
        this._logger.debug("Set symbol trade column: " + tradeColumn + " for category: " + category);
        const map: TSMap<string, TradeSymbol[]> = symbols.categories;
        const newSymbols: TradeSymbol[] = [...map.get(category)];
        newSymbols.sort(SortSymbols(payload.tradeColumn, SortDirection.Ask, quotes.quotes));
        Platform.dispatch(SetSortedSymbols({
            category,
            tradeColumn,
            sortDirection: SortDirection.Ask,
            symbols: newSymbols
        }));
    }

    public doToggleSymbolTradeColumn = (payload: ToggleSymbolColumnSortPayload) => {
        const {symbols, quotes} = Platform.reduxState<StoreState>();
        const {category, tradeColumn} = payload;
        const tables: TSMap<string, SymbolTableState> = symbols.tables;
        const tableState: SymbolTableState = tables.get(category);
        const sort: TSMap<TradeColumn, SortDirection> = tableState.sort;
        const sortDirection: SortDirection = sort.get(payload.tradeColumn);
        const resultDirection: SortDirection = sortDirection ? SortDirection.reverse(sortDirection) : SortDirection.Ask;
        this._logger.debug("Toggle symbol trade column: " + payload.tradeColumn + " for category: " + category + " result direction: " + resultDirection);
        const map: TSMap<string, TradeSymbol[]> = symbols.categories;
        const newSymbols: TradeSymbol[] = [...map.get(category)];
        newSymbols.sort(SortSymbols(payload.tradeColumn, resultDirection, quotes.quotes));
        Platform.dispatch(SetSortedSymbols({
            category,
            tradeColumn,
            sortDirection: resultDirection,
            symbols: newSymbols
        }));
    }

    public doSubscribeSymbol = async (payload: SymbolPayload) => {
        const {SymbolId, symbol} = payload.symbol;
        const subscribedSymbols: TSMap<number, SymbolSubscriptionInfo> = Platform.reduxState<StoreState>().symbols.subscribedSymbols;
        if (!subscribedSymbols.has(SymbolId)) {
            this._logger.debug("Try to subscribe for symbol: " + symbol);
            const subscription: SymbolSubscriptionInfo = {
                SymbolDisplayName: symbol,
                SymbolId,
                ViewInBox: true,
                ViewInGrid: true,
            };
            Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
            const answer: [HttpReject, any] = await Utils.to(this.sendToQuotes([...subscribedSymbols.values(), subscription], "UpdateSymbols"));
            Platform.dispatch(HideLoader({}));
            if (answer[0]) {
                this._logger.warn("Failed subscribe symbol");
                XhrUtil.notifyReject(answer[0]);
            } else {
                this._logger.debug("Symbol " + symbol + " subscribed");
                const {quotes} = Platform.reduxState<StoreState>();
                const symbols: TSMap<number, SymbolSubscriptionInfo> = subscribedSymbols.clone();
                symbols.set(SymbolId, subscription);
                Platform.dispatch(SetSubscribedSymbols({
                    symbols
                }));
                Platform.dispatch(AddSubscribedSymbol({symbol: payload.symbol, quotes: quotes.quotes}));
                Platform.dispatch(AddInboxMessage({
                    message: {
                        type: InboxMessageType.SymbolSubscribed,
                        parameters: [
                            Parameter.Of(InboxParameter.SymbolId, SymbolId)
                        ]
                    }
                }));
            }
        } else {
            this._logger.debug("Already subscribed for symbol: " + symbol);
        }
    }

    public doUnSubscribeSymbol = async (payload: SymbolPayload) => {
        const {SymbolId, symbol} = payload.symbol;
        const subscribedSymbols: TSMap<number, SymbolSubscriptionInfo> = Platform.reduxState<StoreState>().symbols.subscribedSymbols;
        if (subscribedSymbols.has(SymbolId)) {
            this._logger.debug("Try to unsubscribe from symbol: " + symbol);
            const subscriptions: SymbolSubscriptionInfo[] = subscribedSymbols.values().filter((subscribedSymbol: SymbolSubscriptionInfo) => subscribedSymbol.SymbolId !== payload.symbol.SymbolId);
            Platform.dispatch(SetLoader({loaderType: LoaderType.FullScreen}));
            const answer: [HttpReject, any] = await Utils.to(this.sendToQuotes(subscriptions, "UpdateSymbols"));
            Platform.dispatch(HideLoader({}));
            if (answer[0]) {
                this._logger.warn("Failed unsubscribe symbol");
                XhrUtil.notifyReject(answer[0]);
            } else {
                this._logger.debug("Symbol " + symbol + " unsubscribed");
                const symbols: TSMap<number, SymbolSubscriptionInfo> = subscribedSymbols.clone();
                symbols.delete(SymbolId);
                Platform.dispatch(SetSubscribedSymbols({
                    symbols
                }));
                Platform.dispatch(RemoveSubscribedSymbol({symbol: payload.symbol}));
                Platform.dispatch(AddInboxMessage({
                    message: {
                        type: InboxMessageType.SymbolUnSubscribed,
                        parameters: [
                            Parameter.Of(InboxParameter.SymbolId, SymbolId)
                        ]
                    }
                }));
            }
        } else {
            this._logger.debug("Already unsubscribed from symbol: " + symbol);
        }
    }

    private async sendToQuotes(request: any, path: string): Promise<any> {
        return XhrUtil.sendAuthenticated(request, "WebProfitSite/Quotes/" + path);
    }

    private async sendToAccount(request: any, path: string): Promise<any> {
        return XhrUtil.sendAuthenticated(request, "WebProfitServer/AccountStatesService.svc/json/" + path);
    }
}
